/**
 * Iter 85.1 / Phase E6 Sprint 1 — Admin Workforce Analytics Console (D41).
 * ECON-01 + ECON-02 (2026-06-29) — Unified activity pipeline (engine now
 * consumes Desktop-Agent windows projected as synthetic heartbeats);
 * Inactive → Offline rename in the activity-status vocabulary.
 *
 * Read-only company-wide view of daily Activity %, classification buckets
 * (ACTIVE/UNDERUTILIZED/OFFLINE/OVERLOADED/UNCLASSIFIED), CSV export.
 * Drill-through to employee profile available via employee_code link.
 *
 * RBAC: workforce:read
 */
import { useCallback, useEffect, useState } from "react";
import {
    ArrowsClockwise, DownloadSimple, Users, Warning, XCircle, Clock, Info,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import WorkforcePrivacyBanner from "../../components/workforce/WorkforcePrivacyBanner";

const CLASS_BADGE = {
    ACTIVE: "bg-emerald-100 text-emerald-800 border-emerald-200",
    UNDERUTILIZED: "bg-amber-100 text-amber-800 border-amber-200",
    // ECON-02 (2026-06-29) — activity-status "OFFLINE" replaces the
    // overloaded "INACTIVE" label, which is now reserved for employee
    // lifecycle status. Tone shifted from rose → slate to de-emphasise
    // the alarm signal: an offline day for a Desktop-Agent-only user
    // who simply didn't open the web app is informational, not an alert.
    OFFLINE: "bg-slate-100 text-slate-700 border-slate-200",
    OVERLOADED: "bg-orange-100 text-orange-800 border-orange-200",
    UNCLASSIFIED: "bg-slate-100 text-slate-600 border-slate-200",
};

const today = () => new Date().toISOString().slice(0, 10);
const hrs = (m) => (m == null ? "—" : `${(m / 60).toFixed(1)}h`);

export default function WorkforceAnalyticsConsole() {
    const [asOfDate, setAsOfDate] = useState(today());
    const [dept, setDept] = useState("");
    const [bu, setBu] = useState("");
    const [classFilter, setClassFilter] = useState("");
    const [snap, setSnap] = useState(null);
    const [loading, setLoading] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (asOfDate) params.set("as_of_date", asOfDate);
            if (dept) params.set("department", dept);
            if (bu) params.set("business_unit", bu);
            if (classFilter) params.set("classification", classFilter);
            const { data } = await api.get(`/workforce/admin/snapshot?${params.toString()}`);
            setSnap(data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load workforce snapshot"));
        } finally {
            setLoading(false);
        }
    }, [asOfDate, dept, bu, classFilter]);

    useEffect(() => { reload(); }, [reload]);

    const handleExportCsv = async () => {
        try {
            const params = new URLSearchParams();
            if (asOfDate) params.set("as_of_date", asOfDate);
            if (dept) params.set("department", dept);
            if (bu) params.set("business_unit", bu);
            if (classFilter) params.set("classification", classFilter);
            const r = await api.get(`/workforce/admin/snapshot/export.csv?${params}`, { responseType: "blob" });
            const blob = new Blob([r.data], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `WorkforceSnapshot_${asOfDate}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("CSV downloaded");
        } catch (err) {
            toast.error(formatApiError(err, "CSV export failed"));
        }
    };

    const b = snap?.buckets || {};

    return (
        <div className="p-6 space-y-4" data-testid="workforce-admin-console">
            <PageHeader
                title={(
                    <span className="flex items-center gap-2">
                        Workforce Analytics
                        <TooltipProvider delayDuration={200}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        className="inline-flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
                                        aria-label="What does Workforce Analytics show?"
                                        data-testid="workforce-admin-info-tooltip"
                                    >
                                        <Info size={16} weight="bold" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" align="start" className="max-w-md text-xs leading-relaxed">
                                    Daily classification of every employee&apos;s observable activity.
                                    Reads from a <strong>unified signal pipeline</strong>: Desktop-Agent
                                    activity windows, REEDOS web-app heartbeats, and manual / biometric /
                                    bridge attendance check-ins. An employee is classified
                                    {" "}<span className="font-mono">OFFLINE</span> only when no
                                    signal of any kind is observed for the day. Privacy-locked — no
                                    URL, app name, keystroke or screen content is consumed.
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </span>
                )}
                subtitle="Company-wide daily activity snapshot · D41 — read-only · privacy-safe"
                actions={
                    <div className="flex gap-2 items-end">
                        <div>
                            <Label className="text-xs">As-of</Label>
                            <Input type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)}
                                className="w-36" data-testid="workforce-admin-as-of-input" />
                        </div>
                        <div>
                            <Label className="text-xs">Department</Label>
                            <Input value={dept} onChange={(e) => setDept(e.target.value)}
                                className="w-32" placeholder="All" data-testid="workforce-admin-dept-input" />
                        </div>
                        <div>
                            <Label className="text-xs">Business Unit</Label>
                            <Input value={bu} onChange={(e) => setBu(e.target.value)}
                                className="w-32" placeholder="All" data-testid="workforce-admin-bu-input" />
                        </div>
                        <Button size="sm" variant="outline" onClick={reload} disabled={loading}
                            data-testid="workforce-admin-refresh-btn">
                            <ArrowsClockwise size={14} className="mr-1" /> Refresh
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleExportCsv} disabled={!snap}
                            data-testid="workforce-admin-export-btn">
                            <DownloadSimple size={14} className="mr-1" /> Export CSV
                        </Button>
                    </div>
                }
            />

            <WorkforcePrivacyBanner />

            {/* 5 classification buckets */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="workforce-admin-buckets">
                <BucketCard label="Active" value={b.ACTIVE || 0} testId="bucket-ACTIVE"
                    tone="emerald" onClick={() => setClassFilter(classFilter === "ACTIVE" ? "" : "ACTIVE")}
                    active={classFilter === "ACTIVE"} />
                <BucketCard label="Underutilized" value={b.UNDERUTILIZED || 0} testId="bucket-UNDERUTILIZED"
                    tone="amber" onClick={() => setClassFilter(classFilter === "UNDERUTILIZED" ? "" : "UNDERUTILIZED")}
                    active={classFilter === "UNDERUTILIZED"} />
                <BucketCard label="Offline" value={b.OFFLINE || 0} testId="bucket-OFFLINE"
                    tone="slate" onClick={() => setClassFilter(classFilter === "OFFLINE" ? "" : "OFFLINE")}
                    active={classFilter === "OFFLINE"} />
                <BucketCard label="Overloaded" value={b.OVERLOADED || 0} testId="bucket-OVERLOADED"
                    tone="orange" onClick={() => setClassFilter(classFilter === "OVERLOADED" ? "" : "OVERLOADED")}
                    active={classFilter === "OVERLOADED"} />
                <BucketCard label="Unclassified" value={b.UNCLASSIFIED || 0} testId="bucket-UNCLASSIFIED"
                    tone="slate" onClick={() => setClassFilter(classFilter === "UNCLASSIFIED" ? "" : "UNCLASSIFIED")}
                    active={classFilter === "UNCLASSIFIED"} />
            </div>

            <div className="text-xs text-slate-500">
                {snap ? `${snap.items?.length || 0} employees of ${snap.n_employees || 0} match current filters` : ""}
            </div>

            {/* Employee table with classification + reason */}
            <div className="border rounded">
                <div className="max-h-[600px] overflow-y-auto">
                    <table className="w-full text-xs" data-testid="workforce-admin-table">
                        <thead className="bg-slate-50 border-b uppercase text-slate-600 text-[10px] sticky top-0">
                            <tr>
                                <th className="text-left px-2 py-1">Code</th>
                                <th className="text-left px-2 py-1">Name</th>
                                <th className="text-left px-2 py-1">Dept / BU</th>
                                <th className="text-left px-2 py-1">Login</th>
                                <th className="text-left px-2 py-1">Logout</th>
                                <th className="text-right px-2 py-1">Logged-in</th>
                                <th className="text-right px-2 py-1">Active</th>
                                <th className="text-right px-2 py-1">Idle</th>
                                <th className="text-right px-2 py-1">Break</th>
                                <th className="text-right px-2 py-1">Activity %</th>
                                <th className="text-left px-2 py-1">Classification</th>
                                <th className="text-left px-2 py-1">Reason</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(!snap || (snap.items || []).length === 0) && (
                                <tr><td colSpan="12" className="text-center text-slate-400 p-6">
                                    No employee activity records for the selected filters
                                </td></tr>
                            )}
                            {(snap?.items || []).map((it) => (
                                <tr key={it.employee_id} className="border-b hover:bg-slate-50/50"
                                    data-testid={`workforce-row-${it.employee_id}`}>
                                    <td className="px-2 py-1 font-mono text-[10px]">{it.employee_code}</td>
                                    <td className="px-2 py-1">{it.employee_name}</td>
                                    <td className="px-2 py-1 text-[10px]">
                                        {it.department}<br /><span className="text-slate-500">{it.business_unit}</span>
                                    </td>
                                    <td className="px-2 py-1 font-mono text-[10px]">{(it.login_time || "—").slice(11, 16)}</td>
                                    <td className="px-2 py-1 font-mono text-[10px]">{(it.logout_time || "—").slice(11, 16)}</td>
                                    <td className="px-2 py-1 text-right font-mono">{hrs(it.total_logged_in_minutes)}</td>
                                    <td className="px-2 py-1 text-right font-mono">{hrs(it.total_active_minutes)}</td>
                                    <td className="px-2 py-1 text-right font-mono">{hrs(it.total_idle_minutes)}</td>
                                    <td className="px-2 py-1 text-right font-mono">{hrs(it.total_break_minutes)}</td>
                                    <td className="px-2 py-1 text-right font-mono font-semibold">{it.activity_pct}%</td>
                                    <td className="px-2 py-1">
                                        <Badge className={CLASS_BADGE[it.classification] || ""}>
                                            {it.classification}
                                        </Badge>
                                    </td>
                                    <td className="px-2 py-1 text-[10px] text-slate-600 max-w-xs">
                                        {it.classification_reason}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function BucketCard({ label, value, testId, tone, onClick, active }) {
    const toneMap = {
        emerald: "border-emerald-200 bg-emerald-50/40 hover:bg-emerald-100",
        amber: "border-amber-200 bg-amber-50/40 hover:bg-amber-100",
        rose: "border-rose-200 bg-rose-50/40 hover:bg-rose-100",
        orange: "border-orange-200 bg-orange-50/40 hover:bg-orange-100",
        slate: "border-slate-200 bg-slate-50/40 hover:bg-slate-100",
    };
    return (
        <button onClick={onClick}
            className={`border rounded p-3 text-left transition-shadow ${toneMap[tone] || ""} ${active ? "ring-2 ring-blue-400" : ""}`}
            data-testid={testId}>
            <div className="text-[10px] uppercase tracking-wider text-slate-600">{label}</div>
            <div className="text-2xl font-mono font-bold mt-1">{value}</div>
        </button>
    );
}
